import { useEffect, useRef } from "react";
import { fetchEIdAuthenticationStatusFiTupas } from "Shared/util/web-api";
import {
  EIdAuthenticationPurpose,
  EIdAuthenticationStatus,
  EIdAuthenticationStatusResource,
  EIdProvider,
} from "Shared/util/types";
import { useEIdCallbackQueryParams } from "./eid-callback-query-params-hook";
import { useSeBankIdPoller } from "./se-bankid-poller-hook";

export const useEIdCallbackHandler = (
  registerPromise: (p: Promise<any>) => Promise<any>,
  eIdProvider: string,
  authenticationPurpose: EIdAuthenticationPurpose,
  onCallback: () => void,
  onAuthenticationSucceeded: (token: string) => void,
  onAuthenticationFailed: () => void,
  onAuthenticationError: () => void
) => {
  const isInitialRender = useRef(true);
  const callbackQueryParams = useEIdCallbackQueryParams();
  const seBankIdPoller = useSeBankIdPoller(
    registerPromise,
    onAuthenticationSucceeded,
    onAuthenticationFailed,
    onAuthenticationError
  );

  useEffect(() => {
    if (
      !isInitialRender.current ||
      !eIdProvider ||
      !callbackQueryParams.valid()
    )
      return;
    isInitialRender.current = false;

    const queryData = callbackQueryParams.get();
    callbackQueryParams.clear();

    if (!queryData.eIdToken) {
      console.error("Invalid or missing callback token value");
      return;
    }
    if (!queryData.eIdPurpose) {
      console.error("Invalid or missing callback purpose value");
      return;
    }
    /** If hook is called from multiple components, use the one with correct purpose. */
    if (authenticationPurpose?.toString() === queryData.eIdPurpose) return;

    onCallback();

    const handleSeBankIdCallback = (token: string) =>
      seBankIdPoller.start(token, () => {});

    const handleFiTupasCallback = (
      token: string,
      scriveTransactionId: string
    ) => {
      registerPromise(
        fetchEIdAuthenticationStatusFiTupas(token, scriveTransactionId)
      )
        .then((result: EIdAuthenticationStatusResource) => {
          const { status, verificationToken } = result;

          if (
            status === EIdAuthenticationStatus.Completed &&
            verificationToken
          ) {
            onAuthenticationSucceeded(verificationToken);
          }
        })
        .catch(() => onAuthenticationError());
    };

    switch (eIdProvider) {
      case EIdProvider.SeBankId:
        handleSeBankIdCallback(queryData.eIdToken);
        break;

      case EIdProvider.FiTupas: {
        if (
          queryData.scriveEIdStatus === "true" &&
          queryData.scriveEIdTransactionId
        ) {
          handleFiTupasCallback(
            queryData.eIdToken,
            queryData.scriveEIdTransactionId
          );
        } else if (queryData.scriveEIdStatus === "false") {
          onAuthenticationFailed();
        } else {
          onAuthenticationError();
        }
        break;
      }
    }
  }, [
    eIdProvider,
    authenticationPurpose,
    callbackQueryParams,
    seBankIdPoller,
    onCallback,
    onAuthenticationSucceeded,
    onAuthenticationFailed,
    onAuthenticationError,
  ]);
};
